import { useState } from "react";
import { SrIcon } from "@/components/ui/SrIcon";
import { useUpdateProjectRole, type ProjectRoleColumn } from "@/hooks/use-update-project-role";
import { isAssignableUser, type UserLookup } from "@/hooks/use-users";

interface Props {
  projectId: string;
  /** Hvilken rolle-kolonne på `projects` denne feltet styrer. */
  column: ProjectRoleColumn;
  currentUserId: string | null;
  displayName: string;
  allUsers: UserLookup[];
  /** VIB-757: project.updated_at brukes til optimistic locking. */
  projectUpdatedAt: string;
  /**
   * Når false (default true): feltet er kun visning, pencil-knappen skjules.
   * VIB-722c: brukes til å gates redigering på `manage_team`.
   */
  canEdit?: boolean;
}

/**
 * Inline-redigerbar bruker-rolle (Prosjektleder/Kunderådgiver) i Team &
 * eierskap-seksjonen. Klikk på blyanten for å bytte til select; lagrer ved
 * onChange og kollapser tilbake til navn. Generisk på `column` så samme
 * komponent dekker både PL og kunderådgiver.
 */
export function EditableProjectUserField({
  projectId,
  column,
  currentUserId,
  displayName,
  allUsers,
  projectUpdatedAt,
  canEdit = true,
}: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const updateMut = useUpdateProjectRole();

  const handleChange = (userId: string) => {
    updateMut.mutate(
      { projectId, column, userId: userId || null, expectedUpdatedAt: projectUpdatedAt },
      { onSettled: () => setIsEditing(false) },
    );
  };

  if (!canEdit) {
    return <span className="font-medium text-sm">{displayName}</span>;
  }

  if (isEditing) {
    return (
      <select
        value={currentUserId ?? ""}
        autoFocus
        disabled={updateMut.isPending}
        onBlur={() => setIsEditing(false)}
        onChange={(e) => handleChange(e.target.value)}
        className="px-2 py-0.5 text-sm font-medium border rounded bg-background w-full"
      >
        <option value="">—</option>
        {allUsers.filter(isAssignableUser).map((u) => (
          <option key={u.id} value={u.id}>
            {u.full_name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="font-medium text-sm">{displayName}</span>
      <button
        onClick={() => setIsEditing(true)}
        className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted"
      >
        <SrIcon name="pencil" className="w-3 h-3 text-muted-foreground" />
      </button>
    </div>
  );
}
